import React, { useState, useEffect, useRef, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import Pagination from "../../components/pagination/pagination";
import { Alert, Button } from "reactstrap";
import TableModal from "../../components/tableModal";
import LinkLandingPagesTable from "../../components/linkLandingPagesTable";
import ViewLayout from "../../components/viewLayout";
import TableHelper from "../../components/tableHelper";
import ModuleAccessDeniedAlert from "../../components/moduleAccessDeniedAlert";
import { getCookie } from "../../jwt/_helpers/cookie";
import { useFetch } from "../../utils/fetchHook";
import { StateContext } from "../../utils/context";
import { deleteLinkLandingPage, setLinkLandingPages } from "../../redux/linkLandingPages/action";
import {
  getDeletedDemoLandingIds,
  getDemoLandingRows,
  removeDemoLandingRow,
  upsertDemoLandingRow,
} from "../../utils/demoLinkLandingSessionStore";
import { getLinkLandingMockExportPayload } from "../../utils/linkLandingDemoHelpers";
import {
  isBrowserSessionDemo,
  isDemoMode,
  isCopyMockDataEnabled,
} from "../../utils/demoConfig";
import { DEMO_URL_SHARING_NOTICE } from "../../utils/demoUrlSharingNotice";

const mergeById = (baseRows, extraRows) => {
  const map = new Map();
  (baseRows || []).forEach(row => {
    if (row && row.id != null) {
      map.set(String(row.id), row);
    }
  });
  (extraRows || []).forEach(row => {
    if (row && row.id != null) {
      map.set(String(row.id), { ...(map.get(String(row.id)) || {}), ...row });
    }
  });
  return Array.from(map.values());
};

const filterLandingRowsByQuery = (rows, rawQuery) => {
  const queryText = String(rawQuery || "").trim().toLowerCase();
  if (!queryText) {
    return rows;
  }
  return (rows || []).filter(row =>
    [row?.title, row?.short_description, row?.back_url, row?.full_url]
      .some(value => String(value || "").toLowerCase().includes(queryText)),
  );
};

const LinkLandingPages = () => {
  const dispatch = useDispatch();
  const reduxLandingExtras = useSelector(state => state.linkLandingPages?.created || []);
  
  const token = getCookie("token");
  const {currentUser} = useContext(StateContext);

  const didEff1 = useRef(false);
  const didEff2 = useRef(false);
  const [forceUpdate, setForce] = useState(1);
  const [dataCount, setDataCount] = useState(0);
  const [currrentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [query, setQuery] = useState('');
  const [ordering, setOrdering] = useState({order: '-', name: 'created', sort: 'created'});
  const [subUser, setSubUser] = useState('');
  const [subUserEndpoint, setSubUserEndpoint] = useState('');
  const [parentUserOnly, setParentUserOnly] = useState('');
  const shouldUseDemoDelete = isDemoMode() || isBrowserSessionDemo();
  const shouldUseLocalPagination = shouldUseDemoDelete;
  const fetchPage = shouldUseLocalPagination ? 1 : currrentPage;
  const fetchPageSize = shouldUseLocalPagination ? 1000 : pageSize;

  const [apiData, error, loading] = useFetch(
    "GET",
    `link-landingpages/${subUserEndpoint}?ordering=${ordering.order + ordering.name}&page=${fetchPage}&page_size=${fetchPageSize}&search=${query}${parentUserOnly}`,
    token,
    false,
    forceUpdate
  );

  const [tableData, setTableData] = useState([]);

  const [openModal, setToggleModal] = useState(false);
  const [dataModal, setDataModal] = useState(null);
  const [deleteBody, setDeleteBody] = useState(false);
  
  //ajax handlers
  const [success, setSuccess] = useState(false);
  const [errorPut, setError] = useState(false);
  const [mockCopyNotice, setMockCopyNotice] = useState("");

  const handlerValues = { setSuccess, setError, forceUpdate, setForce };

  const handleCopyMockData = () => {
    const payload = getLinkLandingMockExportPayload();
    navigator.clipboard
      .writeText(JSON.stringify(payload, null, 2))
      .then(() => setMockCopyNotice("Mock data copied to clipboard."))
      .catch(() => setMockCopyNotice("Could not copy mock data."));
  };

  const handleDemoDelete = () => {
    if (!dataModal?.id) {
      return;
    }
    removeDemoLandingRow(dataModal.id);
    dispatch(deleteLinkLandingPage(dataModal.id));
    setTableData(prev => prev.filter(row => String(row?.id) !== String(dataModal.id)));
    setDataCount(prev => Math.max(prev - 1, 0));
    setToggleModal(false);
    setDeleteBody(false);
    setDataModal(null);
    setError(false);
    setSuccess(true);
  };

  useEffect(() => {
    if (apiData?.detail === "Invalid page.") {
      setCurrentPage(1);
      return;
    }

    if (!apiData) {
      return;
    }

    const apiRows = Array.isArray(apiData.results) ? apiData.results : [];
    const deletedIds = new Set(getDeletedDemoLandingIds().map(String));
    const visibleApiRows = apiRows.filter(row => !deletedIds.has(String(row?.id)));
    if (Array.isArray(apiData.results)) {
      dispatch(setLinkLandingPages(apiData.results));
    }

    const sessionRows = getDemoLandingRows();
    const mergedRows = mergeById(mergeById(visibleApiRows, sessionRows), reduxLandingExtras)
      .filter(row => !deletedIds.has(String(row?.id)))
      .sort((a, b) => {
      const left = new Date(b?.created || 0).getTime();
      const right = new Date(a?.created || 0).getTime();
      return left - right;
    });

    // Keep demo/local store in sync with visible LLP rows so public /b/:back_url
    // can resolve links even after refresh or opening in a new tab.
    mergedRows.forEach(row => upsertDemoLandingRow(row));

    const filteredRows = filterLandingRowsByQuery(mergedRows, query);

    if (shouldUseLocalPagination) {
      const totalRows = filteredRows.length;
      const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
      const boundedPage = Math.min(currrentPage, totalPages);
      if (boundedPage !== currrentPage) {
        setCurrentPage(boundedPage);
        return;
      }
      const start = (boundedPage - 1) * pageSize;
      const end = start + pageSize;
      setTableData(filteredRows.slice(start, end));
      setDataCount(totalRows);
      return;
    }

    setTableData(filteredRows);
    setDataCount(Number(apiData?.count) || filteredRows.length);
  }, [apiData, currrentPage, dispatch, pageSize, query, reduxLandingExtras, shouldUseLocalPagination]);


  useEffect(() => {
    if (!didEff1.current) {
      didEff1.current = true;
      return;
    }
    if (shouldUseLocalPagination) {
      return;
    }
    setForce(prev => prev + 1);
  }, [currrentPage, ordering.order, ordering.name, pageSize, shouldUseLocalPagination]);


  useEffect(() => {
    if (!didEff2.current) {
      didEff2.current = true;
      return;
    }
    const timeout = setTimeout(() => {
      setForce(prev => prev + 1);
    }, 500)
    return () => clearTimeout(timeout)
  }, [query])

  const paginationProps = {
    pageSize, setPageSize, dataCount, setCurrentPage, currrentPage
  }

  const defaultSorted = [{
    dataField: ordering.sort,
    order: ordering.order === "-" ? "desc" : "asc"
  }] 

  const showCopyMockData = isCopyMockDataEnabled();

  return (
    <>
      {showCopyMockData && (
        <Alert color="light" className="mb-3 py-2">
          <Button size="sm" color="primary" onClick={handleCopyMockData}>
            Copy mock data
          </Button>
          {mockCopyNotice && (
            <span className="text-muted ml-2">{mockCopyNotice}</span>
          )}
          <small className="d-block text-muted mt-1">
            Exports current link landing page rows as JSON (paste to update seed data).
          </small>
        </Alert>
      )}
    {
      (!currentUser.link_in_bio && !loading) ? (
          <ModuleAccessDeniedAlert
            module="link_in_bio"
            fallbackMessage="Currently this feature is only offered to few selected users but the good news is that you will also be able to use it really soon. Keep watching this space!"
          />
      ): (currentUser.link_in_bio && loading) ? (
        <TableHelper loading />
      ) : error ? (
        <TableHelper error />
      ) :  (
        <div>
            <ViewLayout title={"Your link landing pages"} noteAppend={DEMO_URL_SHARING_NOTICE}>
            <LinkLandingPagesTable
              setToggleModal={setToggleModal}
              setDataModal={setDataModal}
              setDeleteBody={setDeleteBody}
              forceUpdate={forceUpdate}
              tableData={tableData}
              defaultSorted={defaultSorted}
              searchQuery={{setQuery, query, ordering, setOrdering}}
              subUserData={{subUser, setSubUser, subUserEndpoint, setSubUserEndpoint, setParentUserOnly}}
            />
            </ViewLayout>
            <Pagination pagination={paginationProps}/>
        </div>
      )}

      {dataModal && (
        <TableModal
          openModal={openModal}
          setToggleModal={setToggleModal}
          dataModal={dataModal}
          values={handlerValues}
          title={
            deleteBody ? `Delete ${dataModal.title}?` : `Edit ${dataModal.name}?`
          }
          body={"Are you sure you want to delete this link landing page?"}
          apiURL={`link-landingpages/${dataModal.id}/`}
          bodyID={dataModal.id}
          deleteBody={deleteBody}
          onDeleteConfirm={shouldUseDemoDelete ? handleDemoDelete : undefined}
        />
      )}
      {success && <Alert color="success">Link Landing Page has been removed!</Alert>}
      {errorPut && (
        <Alert color="danger">
          Something went wrong! Please refresh page and try again!
        </Alert>
      )}
    </>
  );
};

export default LinkLandingPages;
